import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Minus,
  HandCoins,
  CheckCircle2,
  Hourglass,
  Loader2,
  FileWarning,
  CalendarClock,
  CheckCircle,
  ExternalLink,
  XCircle,
  Info,
  Rocket,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { applicationsApi, getErrorMessage } from '../../services/api';
import OvjRecruiterDrillDialog from './OvjRecruiterDrillDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Same INR formatter used elsewhere — K / L / Cr suffixes. */
const fmtINR = (n) => {
  if (!n && n !== 0) return '₹0';
  const num = Number(n);
  if (Number.isNaN(num)) return '₹0';
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(2)} Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(2)} L`;
  if (num >= 1e3) return `₹${(num / 1e3).toFixed(1)} K`;
  return `₹${num}`;
};

const deltaIcon = (pct) => {
  if (pct >= 0.5) return <TrendingUp className="w-3 h-3 text-emerald-600" />;
  if (pct <= -0.5) return <TrendingDown className="w-3 h-3 text-rose-600" />;
  return <Minus className="w-3 h-3 text-slate-400" />;
};

const deltaColour = (pct) => {
  if (pct >= 0.5) return 'text-emerald-700';
  if (pct <= -0.5) return 'text-rose-700';
  return 'text-slate-500';
};

/**
 * Reusable Offer→Joining funnel widget.
 *
 * Three variants of the same data:
 *   • `compact`  — 3 inline KPIs only (Command Center top strip)
 *   • `default`  — KPIs + pipeline pill + top-5 deltas (Dashboard)
 *   • `full`     — KPIs + full delta table (Reports section)
 *
 * Props:
 *   period        — 'today' | 'week' | 'month' | 'quarter' | 'custom'
 *   startDate     — required when period='custom'
 *   endDate       — required when period='custom'
 *   variant       — 'compact' | 'default' | 'full'
 *   testid        — suffix for data-testid
 */

// Per-row pipeline status styling. Declared at module scope so it's never in
// TDZ during the first render (the JSX calls renderPipelineDialog() before
// any in-component `const` declarations are initialised).
const PIPELINE_STATUS_META = {
  paperwork_pending: {
    label: 'Paperwork pending', colour: 'bg-yellow-100 text-yellow-800',
    Icon: FileWarning, iconCls: 'text-yellow-600',
  },
  joining_overdue: {
    label: 'Joining overdue', colour: 'bg-rose-100 text-rose-800',
    Icon: CalendarClock, iconCls: 'text-rose-600',
  },
  on_track: {
    label: 'On track', colour: 'bg-emerald-100 text-emerald-800',
    Icon: CheckCircle, iconCls: 'text-emerald-600',
  },
};

const OfferVsJoiningCard = ({
  period: periodProp = 'month',
  startDate,
  endDate,
  variant = 'default',
  testid = 'ovj',
  showPeriodSelector = false,
}) => {
  const [internalPeriod, setInternalPeriod] = useState(periodProp);
  // If parent doesn't pass dates, use the internal period selector.
  const period = showPeriodSelector ? internalPeriod : periodProp;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Pending Pipeline drill-down state — lazy loaded on first open
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [pipelineItems, setPipelineItems] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  // Quick-action state inside the drill: which row is mid-save / mid-confirm
  const [pipelineSavingId, setPipelineSavingId] = useState(null);
  const [pipelineDropConfirmId, setPipelineDropConfirmId] = useState(null);
  // Joined drill-down (reuses already-loaded deltas list)
  const [joinedOpen, setJoinedOpen] = useState(false);
  // Same pattern as joinedOpen — drives the "Offers Released · N" drill
  // dialog. Previously this tile only deep-linked to Command Center, but
  // that path hides offers whose `offered_ctc` wasn't captured.
  const [offersOpen, setOffersOpen] = useState(false);
  // Per-recruiter contextual drill-down dialog. When a user clicks any
  // number cell (Offers / Joined / Pending / Joined CTC) on the recruiter
  // table, we open this dialog scoped to that (recruiter, metric).
  const [recruiterDrill, setRecruiterDrill] = useState(null);
  // shape: { recruiter_id, recruiter_name, metric: 'offers'|'joined'|'pending' }
  const openRecruiterDrill = useCallback((row, metric) => {
    setRecruiterDrill({
      recruiter_id: row.recruiter_id,
      recruiter_name: row.recruiter_name,
      metric,
    });
  }, []);
  // Per-recruiter breakdown (full variant only — shown below the delta table on /reports)
  const [byRecruiter, setByRecruiter] = useState(null);
  const [byRecruiterLoading, setByRecruiterLoading] = useState(false);
  // Client-side sort for the By-Recruiter table. Backend already returns
  // joined-desc, but recruiters frequently want to flip by Offers / Joined
  // CTC / Conversion / Pending without losing the Top-mover badge state.
  // Persisted to localStorage so the chosen sort survives reloads.
  const [recruiterSort, setRecruiterSort] = useState(() => {
    try {
      const raw = window.localStorage.getItem('ovj-recruiter-sort');
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    return { key: 'joined', dir: 'desc' };
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('ovj-recruiter-sort', JSON.stringify(recruiterSort));
    } catch (_) { /* ignore */ }
  }, [recruiterSort]);
  // Lens toggle: 'stage_event' (default — funnel velocity) vs 'joining_date'
  // (revenue recognition). Precedence:
  //   1. localStorage (user's explicit session choice — sticky across visits)
  //   2. Org-wide default from `/api/organizations/me.default_join_basis`
  //   3. 'stage_event' hard fallback
  // Org default is fetched lazily on mount and only adopted if the user
  // hasn't yet flipped the toggle (localStorage empty).
  const [joinBasis, setJoinBasis] = useState(() => {
    if (typeof window === 'undefined') return 'stage_event';
    return localStorage.getItem('ovj-join-basis') || 'stage_event';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem('ovj-join-basis')) return;
    let cancelled = false;
    axios.get(`${API}/organizations/me`).then((res) => {
      const orgDefault = res.data?.default_join_basis;
      if (!cancelled && (orgDefault === 'stage_event' || orgDefault === 'joining_date')) {
        setJoinBasis(orgDefault);
      }
    }).catch(() => { /* silent — fall back to stage_event */ });
    return () => { cancelled = true; };
  }, []);
  const updateJoinBasis = (next) => {
    setJoinBasis(next);
    try { localStorage.setItem('ovj-join-basis', next); } catch { /* quota */ }
    // Broadcast so the Recruiter Revenue heatmap (and any other surface
    // that aggregates joins) re-fetches with the matching basis. Same
    // signal whether the user picked it here or elsewhere — keeps the
    // two reports reconciled by default.
    try {
      window.dispatchEvent(new CustomEvent('joinBasis:changed', { detail: { basis: next } }));
    } catch { /* ignore */ }
  };
  const navigate = useNavigate();

  const openPipelineDrill = useCallback(async () => {
    setPipelineOpen(true);
    if (pipelineItems !== null) return; // already loaded
    setPipelineLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/offer-vs-joining/pipeline-items`, {
        params: { limit: 200 },
      });
      setPipelineItems(res.data?.items || []);
    } catch (err) {
      setPipelineItems([]);
    } finally {
      setPipelineLoading(false);
    }
  }, [pipelineItems]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { period, delta_limit: variant === 'full' ? 50 : 5, join_basis: joinBasis };
      if (period === 'custom') {
        if (!startDate || !endDate) {
          setData(null);
          setLoading(false);
          return;
        }
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const res = await axios.get(`${API}/analytics/offer-vs-joining`, { params });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate, variant, joinBasis]);

  useEffect(() => { fetchData(); setPipelineItems(null); }, [fetchData]);

  // Fetch per-recruiter breakdown when variant=full (Reports tab) and the
  // window changes. Kept separate from the main fetch so the rest of the card
  // doesn't block on it.
  useEffect(() => {
    if (variant !== 'full') return;
    let cancelled = false;
    const load = async () => {
      setByRecruiterLoading(true);
      try {
        const params = { period, compare: true, join_basis: joinBasis };
        if (period === 'custom') {
          if (!startDate || !endDate) {
            setByRecruiter(null);
            return;
          }
          params.start_date = startDate;
          params.end_date = endDate;
        }
        const res = await axios.get(
          `${API}/analytics/offer-vs-joining/by-recruiter`,
          { params },
        );
        if (!cancelled) setByRecruiter(res.data);
      } catch (err) {
        if (!cancelled) setByRecruiter(null);
      } finally {
        if (!cancelled) setByRecruiterLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [variant, period, startDate, endDate, joinBasis]);

  // Mark this offer as JOINED — backend needs a joining_date in the body.
  // Use the row's existing joining_date if set; otherwise default to today so
  // recruiters don't have to bounce out to the candidate panel.
  const markPipelineRowJoined = useCallback(async (it, e) => {
    if (e) e.stopPropagation();
    if (!it?.application_id || pipelineSavingId) return;
    setPipelineSavingId(it.application_id);
    try {
      const jd = it.joining_date || new Date().toISOString().slice(0, 10);
      await applicationsApi.changeStage(it.application_id, {
        stage: 'joined',
        joining_date: jd,
        remarks: 'Marked as joined from Pending Pipeline drill-down',
        client_feedback: null,
      });
      toast.success(`${it.candidate_name || 'Candidate'} marked as Joined`);
      setPipelineItems((prev) => (prev || []).filter((x) => x.application_id !== it.application_id));
      fetchData(); // refresh KPI tiles
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as joined'));
    } finally {
      setPipelineSavingId(null);
    }
  }, [pipelineSavingId, fetchData]);

  // Mark this offer as DROPPED with reason=lost_contact. Two-step confirm to
  // protect against misclicks.
  const markPipelineRowDropped = useCallback(async (it, e) => {
    if (e) e.stopPropagation();
    if (!it?.application_id || pipelineSavingId) return;
    setPipelineSavingId(it.application_id);
    try {
      await applicationsApi.changeStage(it.application_id, {
        stage: 'dropped_out',
        dropped_reason: 'lost_contact',
        remarks: 'Marked as dropped (ghosted offer) from Pending Pipeline drill-down',
        client_feedback: null,
      });
      toast.success(`${it.candidate_name || 'Candidate'} marked as Dropped`);
      setPipelineItems((prev) => (prev || []).filter((x) => x.application_id !== it.application_id));
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as dropped'));
    } finally {
      setPipelineSavingId(null);
      setPipelineDropConfirmId(null);
    }
  }, [pipelineSavingId, fetchData]);

  if (loading && !data) {
    return (
      <Card className="p-4 flex items-center gap-2 text-sm text-slate-500" data-testid={`${testid}-loading`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading offer → joining stats…
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="p-4 text-sm text-rose-700 bg-rose-50 border-rose-200" data-testid={`${testid}-error`}>
        {error}
      </Card>
    );
  }
  if (!data) return null;

  const f = data.funnel || {};
  const p = data.pipeline || {};
  const ds = data.delta_summary || {};

  // Compact variant — 3 inline cells, slot into Command Center top strip.
  if (variant === 'compact') {
    return (
      <Card className="p-3 bg-gradient-to-r from-emerald-50/40 to-sky-50/40 border-emerald-100" data-testid={testid}>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
            <HandCoins className="w-3.5 h-3.5 text-amber-600" />
            Offer → Joining · {data.period_label}
          </div>
          <div className="flex items-center gap-3 ml-auto flex-wrap">
            <div className="text-sm">
              <span className="font-mono font-bold text-slate-900">{f.offer_count}</span>
              <span className="text-[10px] text-slate-500 ml-1">offers · {fmtINR(f.offer_ctc_total)}</span>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
            <div className="text-sm">
              <span className="font-mono font-bold text-emerald-700">{f.joined_count}</span>
              <span className="text-[10px] text-slate-500 ml-1">joined · {fmtINR(f.joined_ctc_total)}</span>
            </div>
            <Badge variant="outline" className="text-[10px] font-mono">
              {f.conversion_pct}% conv
            </Badge>
            {p.count > 0 ? (
              <button
                type="button"
                onClick={openPipelineDrill}
                data-testid={`${testid}-pending-drill-trigger`}
                title="Click to see every candidate currently in offer_released"
                className="focus:outline-none focus:ring-2 focus:ring-amber-300 rounded-full"
              >
                <Badge className="bg-amber-100 hover:bg-amber-200 transition text-amber-800 text-[10px] cursor-pointer">
                  <Hourglass className="w-3 h-3 mr-0.5" />
                  {p.count} pending · {fmtINR(p.ctc_total)}
                  <ExternalLink className="w-2.5 h-2.5 ml-1" />
                </Badge>
              </button>
            ) : null}
          </div>
        </div>
        {renderPipelineDialog()}
        {renderJoinedDialog()}
        {renderOffersDialog()}
      </Card>
    );
  }

  // Reusable info-icon tooltip — keeps the JSX below short.
  const InfoTip = ({ id, title, body }) => (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); } }}
            role="button"
            tabIndex={0}
            aria-label={`What does ${title} mean?`}
            data-testid={`${testid}-info-${id}`}
            className="inline-flex items-center justify-center w-3 h-3 rounded-full text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400 cursor-help"
          >
            <Info className="w-3 h-3" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[300px] text-[11px] leading-relaxed">
          <p className="font-semibold mb-1 text-[10px] uppercase tracking-wide opacity-70">{title}</p>
          <p>{body}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Shared header block for default + full variants.
  const KpiGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div
        className={`rounded-lg border border-amber-100 bg-amber-50/40 p-3 ${f.offer_count > 0 ? 'cursor-pointer hover:bg-amber-100/60 hover:border-amber-300 transition' : ''}`}
        data-testid={`${testid}-kpi-offers`}
        onClick={f.offer_count > 0 ? () => setOffersOpen(true) : undefined}
        role={f.offer_count > 0 ? 'button' : undefined}
        tabIndex={f.offer_count > 0 ? 0 : undefined}
        onKeyDown={(e) => { if (f.offer_count > 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOffersOpen(true); } }}
      >
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold flex items-center gap-1">
            Offers Released
            <InfoTip
              id="offers"
              title="Offers Released"
              body="Count of distinct applications that received an OFFER_RELEASED stage event inside the selected window. Uses stage_history.date, not document last-updated. Same app counted once even if offered twice."
            />
          </span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold font-mono text-slate-900">{f.offer_count}</span>
          <span className="text-[11px] text-slate-500">{fmtINR(f.offer_ctc_total)}</span>
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {f.offer_count > 0 ? 'click to see who got offers' : 'no offers in window'}
        </div>
      </div>

      <div
        className={`rounded-lg border border-emerald-100 bg-emerald-50/40 p-3 ${f.joined_count > 0 ? 'cursor-pointer hover:bg-emerald-100/60 hover:border-emerald-300 transition' : ''}`}
        data-testid={`${testid}-kpi-joined`}
        onClick={f.joined_count > 0 ? () => setJoinedOpen(true) : undefined}
        role={f.joined_count > 0 ? 'button' : undefined}
        tabIndex={f.joined_count > 0 ? 0 : undefined}
        onKeyDown={(e) => { if (f.joined_count > 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setJoinedOpen(true); } }}
      >
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold flex items-center gap-1">
            Joined
            <InfoTip
              id="joined"
              title="Joined"
              body="Count of applications whose JOINED stage event falls inside the selected window. Uses the JOINED transition date, not the document's last-updated time. Each candidate counts once."
            />
          </span>
          {f.joined_count > 0 ? <ExternalLink className="w-3 h-3 text-emerald-500" /> : null}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold font-mono text-slate-900">{f.joined_count}</span>
          <span className="text-[11px] text-slate-500">{fmtINR(f.joined_ctc_total)}</span>
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {f.joined_count > 0 ? 'click to see who joined' : 'no joins in window'}
        </div>
      </div>

      <div
        className={`rounded-lg border border-violet-100 bg-violet-50/40 p-3 ${f.joined_count > 0 ? 'cursor-pointer hover:bg-violet-100/60 hover:border-violet-300 transition' : ''}`}
        data-testid={`${testid}-kpi-conversion`}
        onClick={f.joined_count > 0 ? () => setJoinedOpen(true) : undefined}
        role={f.joined_count > 0 ? 'button' : undefined}
        tabIndex={f.joined_count > 0 ? 0 : undefined}
        onKeyDown={(e) => { if (f.joined_count > 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setJoinedOpen(true); } }}
      >
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold flex items-center gap-1">
            Conversion
            <InfoTip
              id="conversion"
              title="Conversion"
              body="Joined ÷ Offers Released × 100 for the selected window. Can exceed 100% when joins in the window come from offers issued before the window — that's mathematically correct, not a bug."
            />
          </span>
          {f.joined_count > 0 ? <ExternalLink className="w-3 h-3 text-violet-500" /> : null}
        </div>
        <div className="text-2xl font-bold font-mono text-slate-900">{f.conversion_pct}%</div>
        <div className="text-[10px] text-slate-500 mt-0.5">joins ÷ offers in window</div>
      </div>

      <div
        className={`rounded-lg border border-sky-100 bg-sky-50/40 p-3 ${p.count > 0 ? 'cursor-pointer hover:bg-sky-100/60 hover:border-sky-300 transition' : ''}`}
        data-testid={`${testid}-kpi-pipeline`}
        onClick={p.count > 0 ? openPipelineDrill : undefined}
        role={p.count > 0 ? 'button' : undefined}
        tabIndex={p.count > 0 ? 0 : undefined}
        onKeyDown={(e) => { if (p.count > 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openPipelineDrill(); } }}
      >
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span className="text-[10px] uppercase tracking-wide text-sky-700 font-semibold flex items-center gap-1">
            Pending Pipeline
            <InfoTip
              id="pipeline"
              title="Pending Pipeline"
              body="Snapshot count of applications currently in OFFER_RELEASED stage (not bounded by the window — this is the live forecast). ₹ sum excludes any offered_ctc above ₹50 Cr as a sanity guard."
            />
          </span>
          {p.count > 0 ? <ExternalLink className="w-3 h-3 text-sky-500" /> : null}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold font-mono text-slate-900">{p.count}</span>
          <span className="text-[11px] text-slate-500">{fmtINR(p.ctc_total)}</span>
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {p.count > 0 ? 'click to inspect paperwork' : 'currently in offer_released'}
        </div>
      </div>
    </div>
  );

  // ---------- Per-recruiter breakdown table (Reports tab) ----------
  // Sorted joined-desc by backend. Shows who's contributing to the funnel +
  // who's lagging. Click a row → opens Command Center scoped to that recruiter.
  const RecruiterBreakdownTable = () => {
    if (byRecruiterLoading && !byRecruiter) {
      return (
        <div className="flex items-center justify-center py-6 text-xs text-slate-500">
          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Loading recruiter breakdown…
        </div>
      );
    }
    const rawRows = byRecruiter?.rows || [];
    if (rawRows.length === 0) {
      return (
        <div className="text-xs text-slate-500 italic py-3" data-testid={`${testid}-recruiter-empty`}>
          No recruiter activity in this window. Switch the period above to widen the range.
        </div>
      );
    }
    const totals = byRecruiter?.totals || {};

    // Sortable column extractors — keep recruiter name as the tiebreaker so
    // ordering is deterministic when values match (otherwise React would
    // reshuffle equal rows on every re-render).
    const sortKeyFns = {
      recruiter: (r) => (r.recruiter_name || '').toLowerCase(),
      offers: (r) => r.offers_released || 0,
      joined: (r) => r.joined || 0,
      pending: (r) => r.pending || 0,
      joined_ctc: (r) => r.joined_ctc_total || 0,
      conversion: (r) => r.conversion_pct || 0,
    };
    const fn = sortKeyFns[recruiterSort.key] || sortKeyFns.joined;
    const rows = [...rawRows].sort((a, b) => {
      const va = fn(a);
      const vb = fn(b);
      let cmp;
      if (typeof va === 'string') cmp = va.localeCompare(vb);
      else cmp = va - vb;
      if (cmp === 0) {
        // Tiebreaker on name asc to keep the order stable across renders
        cmp = (a.recruiter_name || '').localeCompare(b.recruiter_name || '');
      }
      return recruiterSort.dir === 'asc' ? cmp : -cmp;
    });

    const toggleSort = (key) => {
      setRecruiterSort((cur) => {
        if (cur.key !== key) {
          // For non-text columns, default to descending (more interesting first)
          return { key, dir: key === 'recruiter' ? 'asc' : 'desc' };
        }
        return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
      });
    };
    const SortIcon = ({ k }) => {
      if (recruiterSort.key !== k) {
        return <ArrowUpDown className="w-3 h-3 opacity-40" />;
      }
      return recruiterSort.dir === 'asc'
        ? <ArrowUp className="w-3 h-3 text-teal-600" />
        : <ArrowDown className="w-3 h-3 text-teal-600" />;
    };
    const SortHead = ({ k, align = 'right', children }) => (
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`flex items-center gap-1 hover:text-teal-700 transition w-full ${
          align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''
        } ${recruiterSort.key === k ? 'text-teal-700' : ''}`}
        data-testid={`${testid}-recruiter-sort-${k}`}
        aria-pressed={recruiterSort.key === k}
        title={`Sort by ${k}`}
      >
        <span>{children}</span>
        <SortIcon k={k} />
      </button>
    );
    // Deterministic per-name colour for the avatar chip
    const palette = ['bg-sky-100 text-sky-700', 'bg-emerald-100 text-emerald-700',
                     'bg-amber-100 text-amber-700', 'bg-violet-100 text-violet-700',
                     'bg-rose-100 text-rose-700', 'bg-teal-100 text-teal-700'];
    const chipFor = (name) => {
      const hash = (name || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
      return palette[hash % palette.length];
    };

    // Top mover = biggest positive joined_delta. Award is shared if multiple
    // recruiters tie at the top (no arbitrary tie-break). Only meaningful when
    // compare data is loaded AND at least one recruiter posted a positive gain.
    let topMoverDelta = 0;
    if (byRecruiter?.compare) {
      topMoverDelta = rows.reduce((max, r) => Math.max(max, r.joined_delta || 0), 0);
    }
    const isTopMover = (r) => topMoverDelta > 0 && (r.joined_delta || 0) === topMoverDelta;

    return (
      <div className="space-y-1" data-testid={`${testid}-recruiter-table`}>
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold pb-1.5 border-b border-slate-100">
          <div className="col-span-5">
            <SortHead k="recruiter" align="left">Recruiter</SortHead>
          </div>
          <div className="col-span-2">
            <SortHead k="offers">Offers</SortHead>
          </div>
          <div className="col-span-2">
            <SortHead k="joined">Joined</SortHead>
          </div>
          <div className="col-span-1">
            <SortHead k="pending">Pending</SortHead>
          </div>
          <div className="col-span-2">
            <SortHead k="joined_ctc">Joined CTC</SortHead>
          </div>
        </div>
        {rows.map((r) => (
          <div
            key={r.recruiter_id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/my-performance?user_id=${r.recruiter_id}&period=${period}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/my-performance?user_id=${r.recruiter_id}&period=${period}`);
              }
            }}
            className="w-full grid grid-cols-12 gap-2 text-xs items-center py-1.5 px-1 rounded hover:bg-slate-50 transition text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            data-testid={`${testid}-recruiter-row-${r.recruiter_id}`}
            title={`Open ${r.recruiter_name}'s performance dashboard`}
          >
            <div className="col-span-5 min-w-0 flex items-center gap-2">
              <span
                className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${chipFor(r.recruiter_name)} flex-shrink-0`}
              >
                {(r.recruiter_name || '?').trim().slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                  {r.recruiter_name}
                  {isTopMover(r) && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-700 bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300 rounded px-1.5 py-0.5"
                      title={`Biggest gain this period — +${topMoverDelta} vs ${byRecruiter.prev_period_label || 'previous'}`}
                      data-testid={`${testid}-recruiter-top-mover-${r.recruiter_id}`}
                    >
                      <Rocket className="w-2.5 h-2.5" /> Top mover
                    </span>
                  )}
                  {r.is_active === false && (
                    <Badge variant="outline" className="text-[9px] py-0 px-1 border-slate-300 text-slate-500">
                      inactive
                    </Badge>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 truncate">
                  {r.conversion_pct.toFixed(1)}% conversion
                </div>
              </div>
            </div>
            <div className="col-span-2 text-right">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openRecruiterDrill(r, 'offers'); }}
                className="inline-flex flex-col items-end gap-0 rounded px-1.5 py-0.5 hover:bg-amber-50 hover:text-amber-800 transition group"
                data-testid={`${testid}-recruiter-offers-${r.recruiter_id}`}
                title={`Open ${r.recruiter_name}'s offers in ${data?.period_label}`}
              >
                <span className="font-mono text-slate-700 group-hover:underline decoration-dotted underline-offset-2">{r.offers_released}</span>
                <span className="text-[10px] text-slate-400 font-mono">{fmtINR(r.offer_ctc_total)}</span>
              </button>
            </div>
            <div className="col-span-2 text-right">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openRecruiterDrill(r, 'joined'); }}
                className="inline-flex flex-col items-end gap-0 rounded px-1.5 py-0.5 hover:bg-emerald-50 transition group"
                data-testid={`${testid}-recruiter-joined-${r.recruiter_id}`}
                title={`Open ${r.recruiter_name}'s joined candidates in ${data?.period_label}`}
              >
                <div className="font-mono font-semibold text-emerald-700 flex items-center justify-end gap-1.5 group-hover:underline decoration-dotted underline-offset-2">
                {r.joined}
                {byRecruiter?.compare && (() => {
                  const d = r.joined_delta ?? 0;
                  const prev = r.joined_prev ?? 0;
                  const label = byRecruiter.prev_period_label || 'prev';
                  if (d > 0) {
                    return (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded px-1 py-0.5"
                        title={`+${d} vs ${label} (was ${prev})`}
                        data-testid={`${testid}-recruiter-trend-${r.recruiter_id}`}
                      >
                        <TrendingUp className="w-2.5 h-2.5" /> +{d}
                      </span>
                    );
                  }
                  if (d < 0) {
                    return (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-bold text-rose-600 bg-rose-50 rounded px-1 py-0.5"
                        title={`${d} vs ${label} (was ${prev})`}
                        data-testid={`${testid}-recruiter-trend-${r.recruiter_id}`}
                      >
                        <TrendingDown className="w-2.5 h-2.5" /> {d}
                      </span>
                    );
                  }
                  // Zero delta only worth showing if there's prev activity, otherwise it's noise
                  if (prev > 0) {
                    return (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1 py-0.5"
                        title={`Flat vs ${label} (was ${prev})`}
                        data-testid={`${testid}-recruiter-trend-${r.recruiter_id}`}
                      >
                        <Minus className="w-2.5 h-2.5" /> 0
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="text-[10px] text-slate-400 font-mono">{fmtINR(r.joined_ctc_total)}</div>
              </button>
            </div>
            <div className="col-span-1 text-right">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openRecruiterDrill(r, 'pending'); }}
                disabled={!(r.pending > 0)}
                className={`font-mono rounded px-1.5 py-0.5 transition ${r.pending > 0 ? 'text-amber-700 font-semibold hover:bg-amber-50 hover:underline decoration-dotted underline-offset-2' : 'text-slate-400 cursor-default'}`}
                data-testid={`${testid}-recruiter-pending-${r.recruiter_id}`}
                title={r.pending > 0 ? `${r.recruiter_name}: ${r.pending} pending offers` : 'No pending offers'}
              >
                {r.pending}
              </button>
            </div>
            <div className="col-span-2 text-right">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openRecruiterDrill(r, 'joined'); }}
                disabled={!(r.joined_ctc_total > 0)}
                className={`font-mono font-bold rounded px-1.5 py-0.5 transition ${r.joined_ctc_total > 0 ? 'text-slate-900 hover:bg-emerald-50 hover:underline decoration-dotted underline-offset-2' : 'text-slate-400 cursor-default'}`}
                data-testid={`${testid}-recruiter-joined-ctc-${r.recruiter_id}`}
                title={r.joined_ctc_total > 0 ? `${r.recruiter_name}: ${fmtINR(r.joined_ctc_total)} joined CTC` : 'No joined CTC yet'}
              >
                {fmtINR(r.joined_ctc_total)}
              </button>
            </div>
          </div>
        ))}
        <div className="grid grid-cols-12 gap-2 text-xs items-center pt-2 mt-1 border-t border-slate-200 font-semibold">
          <div className="col-span-5 text-[11px] uppercase tracking-wide text-slate-600">
            Team total · {rows.length} recruiter{rows.length === 1 ? '' : 's'}
          </div>
          <div className="col-span-2 text-right font-mono text-slate-700">{totals.offers_released ?? 0}</div>
          <div className="col-span-2 text-right font-mono text-emerald-700 flex items-center justify-end gap-1.5">
            {totals.joined ?? 0}
            {byRecruiter?.compare && (() => {
              const d = totals.joined_delta ?? 0;
              const prev = totals.joined_prev ?? 0;
              const label = byRecruiter.prev_period_label || 'prev';
              if (d > 0) return <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded px-1 py-0.5" title={`+${d} vs ${label} (was ${prev})`}><TrendingUp className="w-2.5 h-2.5" /> +{d}</span>;
              if (d < 0) return <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-rose-600 bg-rose-50 rounded px-1 py-0.5" title={`${d} vs ${label} (was ${prev})`}><TrendingDown className="w-2.5 h-2.5" /> {d}</span>;
              if (prev > 0) return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1 py-0.5" title={`Flat vs ${label} (was ${prev})`}><Minus className="w-2.5 h-2.5" /> 0</span>;
              return null;
            })()}
          </div>
          <div className="col-span-1 text-right font-mono text-amber-700">{totals.pending ?? 0}</div>
          <div className="col-span-2 text-right font-mono text-slate-900">{fmtINR(totals.joined_ctc_total || 0)}</div>
        </div>
      </div>
    );
  };


  const DeltaTable = ({ rows, max }) => (
    <div className="space-y-1.5" data-testid={`${testid}-delta-table`}>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3">
          No per-candidate deltas tracked for this window. Deltas require the offer_released stage to have captured `offered_ctc` — newer joins will populate this automatically.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-slate-500 font-semibold pb-1 border-b border-slate-100">
            <div className="col-span-5">Candidate</div>
            <div className="col-span-2 text-right">Offered</div>
            <div className="col-span-2 text-right">Joined</div>
            <div className="col-span-3 text-right">Δ</div>
          </div>
          {rows.slice(0, max).map((d) => (
            <div
              key={d.application_id}
              className="grid grid-cols-12 gap-2 text-xs items-center py-1"
              data-testid={`${testid}-delta-row-${d.application_id}`}
            >
              <div className="col-span-5 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{d.candidate_name || 'Unknown'}</div>
                <div className="text-[10px] text-slate-500 truncate">{d.job_title || '—'}{d.client_name ? ` · ${d.client_name}` : ''}</div>
              </div>
              <div className="col-span-2 text-right font-mono text-slate-700">{fmtINR(d.offered_ctc)}</div>
              <div className="col-span-2 text-right font-mono text-slate-700">{fmtINR(d.joined_ctc)}</div>
              <div className={`col-span-3 text-right flex items-center gap-1 justify-end font-mono font-semibold ${deltaColour(d.delta_pct)}`}>
                {deltaIcon(d.delta_pct)}
                {d.delta_pct >= 0 ? '+' : ''}{d.delta_pct.toFixed(1)}%
                <span className="text-[10px] font-normal text-slate-400 ml-1">({fmtINR(Math.abs(d.delta_abs))})</span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );

  const SummaryChips = () => (
    <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
      <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        {ds.matched} matched
      </Badge>
      <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700">
        <TrendingUp className="w-3 h-3 mr-1" />
        {ds.higher} higher
      </Badge>
      <Badge variant="outline" className="bg-rose-50 border-rose-200 text-rose-700">
        <TrendingDown className="w-3 h-3 mr-1" />
        {ds.lower} lower
      </Badge>
      <span className="text-[10px] text-slate-500 ml-1">{ds.tracked} tracked</span>
    </div>
  );

  // ----- Pending Pipeline drill-down dialog -----
  // Renders a per-row paperwork-status list so the user can spot stalled offers
  // without bouncing to another page.
  // STATUS_META is intentionally declared at module scope (above the component)
  // — declaring it inside this function-component as `const` would put it in
  // TDZ during the first render, and the JSX above calls `renderPipelineDialog()`
  // (which references STATUS_META) before this point in the source. Module-scope
  // sidesteps the temporal dead zone entirely.

  // Note: declared as a function (not const arrow) so it's hoisted — the
  // compact-variant return above references it before this line of source.
  function renderPipelineDialog() {
    return (
    <Dialog open={pipelineOpen} onOpenChange={(open) => {
      setPipelineOpen(open);
      if (!open) setPipelineDropConfirmId(null);
    }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid={`${testid}-pipeline-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hourglass className="w-4 h-4 text-amber-600" />
            Pending Pipeline · {p.count} candidates · {fmtINR(p.ctc_total)}
          </DialogTitle>
          <DialogDescription>
            Every candidate currently in <strong>offer_released</strong> stage. Sorted by urgency — paperwork-pending first, then joining-overdue, then on-track. Click any row to open it in the Command Center.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {pipelineLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : !pipelineItems || pipelineItems.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-6 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              No candidates in offer_released — pipeline is clean.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid={`${testid}-pipeline-list`}>
              {pipelineItems.map((it) => {
                const meta = PIPELINE_STATUS_META[it.status] || PIPELINE_STATUS_META.on_track;
                const Icon = meta.Icon;
                const isSaving = pipelineSavingId === it.application_id;
                const inConfirm = pipelineDropConfirmId === it.application_id;
                return (
                  <li key={it.application_id}>
                    <div
                      className="block rounded-md border border-slate-200 hover:border-amber-300 hover:bg-amber-50/30 transition px-3 py-2.5"
                      data-testid={`${testid}-pipeline-row-${it.application_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.iconCls}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900 truncate">
                              {it.candidate_name || 'Unnamed'}
                            </span>
                            <Badge className={`text-[10px] ${meta.colour}`}>{meta.label}</Badge>
                            {it.days_pending != null && (
                              <Badge variant="outline" className="text-[10px]">
                                {it.days_pending}d pending
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate mt-0.5">
                            {it.job_title || '—'}{it.client_name ? ` · ${it.client_name}` : ''}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500 flex-wrap">
                            <span className="font-mono">{fmtINR(it.offered_ctc)}</span>
                            <span>
                              Offer letter: {it.offer_letter_date ? (
                                <span className="text-emerald-700 font-medium">{it.offer_letter_date}</span>
                              ) : (
                                <span className="text-rose-700 font-medium">not set</span>
                              )}
                            </span>
                            <span>
                              Joining: {it.joining_date ? (
                                <span className="font-medium">{it.joining_date}</span>
                              ) : (
                                <span className="text-slate-400">not set</span>
                              )}
                            </span>
                          </div>
                        </div>
                        {/* Quick actions — two-step confirm for the dropped path */}
                        <div className="flex items-center gap-1.5 flex-shrink-0 self-center">
                          {inConfirm ? (
                            <>
                              <Button
                                size="sm"
                                type="button"
                                onClick={(e) => markPipelineRowDropped(it, e)}
                                disabled={isSaving || !!pipelineSavingId}
                                data-testid={`${testid}-confirm-drop-${it.application_id}`}
                                className="h-7 px-2.5 text-[11px] font-semibold bg-rose-600 hover:bg-rose-700 text-white"
                              >
                                {isSaving ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Dropping</>
                                ) : 'Confirm drop'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setPipelineDropConfirmId(null); }}
                                disabled={isSaving}
                                data-testid={`${testid}-cancel-drop-${it.application_id}`}
                                className="h-7 px-2 text-[11px] text-slate-600 hover:text-slate-900"
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="default"
                                type="button"
                                onClick={(e) => markPipelineRowJoined(it, e)}
                                disabled={isSaving || !!pipelineSavingId}
                                data-testid={`${testid}-mark-joined-${it.application_id}`}
                                className="h-7 px-2.5 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                                title="Mark as Joined (uses existing joining date if set, else today)"
                              >
                                {isSaving ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving</>
                                ) : (
                                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Joined</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setPipelineDropConfirmId(it.application_id); }}
                                disabled={isSaving || !!pipelineSavingId}
                                data-testid={`${testid}-mark-dropped-${it.application_id}`}
                                className="h-7 px-2.5 text-[11px] font-semibold border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300"
                                title="Mark as Dropped (lost_contact)"
                              >
                                <XCircle className="w-3 h-3 mr-1" /> Drop
                              </Button>
                              <a
                                href={`/command-center?app=${it.application_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-slate-400 hover:text-amber-600"
                                title="Open in Command Center"
                                data-testid={`${testid}-open-cc-${it.application_id}`}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
    );
  }

  // Joined drill — uses the full `joined_candidates` list so every candidate
  // who joined in the window appears here, even when their offer→join CTC
  // delta wasn't trackable (no offer_released event with captured
  // offered_ctc). Tracked rows surface delta info, untracked ones show a
  // muted "—" with a tooltip pointing the user at the missing field.
  function renderJoinedDialog() {
    const rows = data?.joined_candidates || [];
    return (
    <Dialog open={joinedOpen} onOpenChange={setJoinedOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid={`${testid}-joined-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Joined · {f.joined_count} candidates · {fmtINR(f.joined_ctc_total)}
          </DialogTitle>
          <DialogDescription>
            Candidates whose JOINED stage event falls inside {data?.period_label}.
            Rows with a tracked offer→join CTC delta surface first; the rest
            are shown below with a muted "—" — capture <code className="text-[10px] bg-slate-100 px-1 py-px rounded">offered_ctc</code> at OFFER_RELEASED to start tracking.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {rows.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-6" data-testid={`${testid}-joined-empty`}>
              No candidates joined in this window.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid={`${testid}-joined-table`}>
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Candidate</th>
                  <th className="text-left px-2 py-1.5">Role · Client</th>
                  <th className="text-left px-2 py-1.5">Recruiter</th>
                  <th className="text-right px-2 py-1.5">Offered</th>
                  <th className="text-right px-2 py-1.5">Joined</th>
                  <th className="text-right px-2 py-1.5">Δ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.application_id || i}
                    className={`border-t border-slate-100 ${r.tracked ? '' : 'opacity-70'}`}
                    data-testid={`${testid}-joined-row-${i}`}
                    data-tracked={r.tracked ? 'true' : 'false'}
                  >
                    <td className="px-2 py-1.5 font-medium text-slate-800">
                      {r.candidate_name || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">
                      {[r.job_title, r.client_name].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">
                      {r.recruiter_name || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-700 font-mono">
                      {r.offered_ctc ? fmtINR(r.offered_ctc) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-700 font-mono">
                      {r.joined_ctc ? fmtINR(r.joined_ctc) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {r.tracked ? (
                        <span className="inline-flex items-center gap-1">
                          {deltaIcon(r.delta_pct || 0)}
                          <span className={
                            (r.delta_pct || 0) >= 0.5
                              ? 'text-emerald-700'
                              : (r.delta_pct || 0) <= -0.5
                              ? 'text-rose-700'
                              : 'text-slate-500'
                          }>
                            {(r.delta_pct || 0) > 0 ? '+' : ''}{(r.delta_pct || 0).toFixed(1)}%
                          </span>
                        </span>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-slate-400 cursor-help">—</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              No offered_ctc captured at OFFER_RELEASED — can't compute delta.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
    );
  }

  // Offers Released drill — mirrors the Joined dialog. Lists every
  // OFFER_RELEASED event in the window so recruiters see candidates even
  // when offered_ctc wasn't captured. Rows that are still sitting in
  // OFFER_RELEASED (haven't joined or dropped) get a yellow "Pending"
  // chip so the user can spot offers that need follow-up.
  function renderOffersDialog() {
    const rows = data?.offer_candidates || [];
    return (
    <Dialog open={offersOpen} onOpenChange={setOffersOpen}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid={`${testid}-offers-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins className="w-4 h-4 text-amber-600" />
            Offers Released · {f.offer_count} candidates · {fmtINR(f.offer_ctc_total)}
          </DialogTitle>
          <DialogDescription>
            Every offer released inside {data?.period_label}. Rows without a captured <code className="text-[10px] bg-slate-100 px-1 py-px rounded">offered_ctc</code> show "—" — add the figure on the candidate's stage entry so revenue maths stay accurate.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {rows.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-6" data-testid={`${testid}-offers-empty`}>
              No offers released in this window.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid={`${testid}-offers-table`}>
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Candidate</th>
                  <th className="text-left px-2 py-1.5">Role · Client</th>
                  <th className="text-left px-2 py-1.5">Recruiter</th>
                  <th className="text-right px-2 py-1.5">Offered CTC</th>
                  <th className="text-center px-2 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.application_id || i}
                    className={`border-t border-slate-100 ${r.tracked ? '' : 'opacity-70'}`}
                    data-testid={`${testid}-offers-row-${i}`}
                    data-tracked={r.tracked ? 'true' : 'false'}
                  >
                    <td className="px-2 py-1.5 font-medium text-slate-800">
                      {r.candidate_name || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">
                      {[r.job_title, r.client_name].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">
                      {r.recruiter_name || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-700 font-mono">
                      {r.offered_ctc ? (
                        fmtINR(r.offered_ctc)
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-slate-400 cursor-help">—</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              No offered_ctc captured at OFFER_RELEASED. Add it on the candidate's stage entry.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {r.still_in_offer_stage ? (
                        <span className="inline-flex items-center px-1.5 py-px rounded bg-amber-100 text-amber-800 text-[10px] font-medium">
                          Pending join
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-px rounded bg-slate-100 text-slate-600 text-[10px] font-medium">
                          Resolved
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
    );
  }

  return (
    <Card className="p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        <HandCoins className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-semibold text-slate-900">Offer → Joining</h3>
        <span className="text-[11px] text-slate-500">· {data.period_label}</span>
        {showPeriodSelector ? (
          <select
            value={internalPeriod}
            onChange={(e) => setInternalPeriod(e.target.value)}
            className="ml-2 h-7 rounded-md border border-slate-200 bg-white px-2 text-xs focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
            data-testid={`${testid}-period-select`}
          >
            <option value="today">Today</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="quarter">This quarter</option>
          </select>
        ) : null}

        {/* Lens toggle: stage_event = funnel velocity; joining_date = actual revenue.
            Hidden on the compact `default` variant to keep Dashboard tile clean. */}
        {variant !== 'default' && (
          <div
            className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[10px] font-semibold"
            role="group"
            aria-label="Joined bucket lens"
            data-testid={`${testid}-basis-toggle`}
          >
            <button
              type="button"
              onClick={() => updateJoinBasis('stage_event')}
              className={`px-2 py-1 rounded transition ${
                joinBasis === 'stage_event'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              title="Bucket joins by when the JOINED stage was set — measures funnel velocity"
              data-testid={`${testid}-basis-stage-event`}
            >
              By stage event
            </button>
            <button
              type="button"
              onClick={() => updateJoinBasis('joining_date')}
              className={`px-2 py-1 rounded transition ${
                joinBasis === 'joining_date'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              title="Bucket joins by the candidate's contractual joining_date — measures actual headcount/revenue this window"
              data-testid={`${testid}-basis-joining-date`}
            >
              By joining date
            </button>
          </div>
        )}
        <div className="ml-auto">
          <SummaryChips />
        </div>
      </div>
      <KpiGrid />
      <div className="pt-2 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">
          Per-candidate CTC delta · biggest swings first
        </div>
        <DeltaTable rows={data.deltas || []} max={variant === 'full' ? 50 : 5} />
      </div>
      {variant === 'full' && (
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
              By recruiter · {byRecruiter?.period_label || data?.period_label || ''}
            </div>
            <div className="text-[10px] text-slate-400">
              Click a number for the contextual list · click a recruiter's name for their dashboard
            </div>
          </div>
          <RecruiterBreakdownTable />
        </div>
      )}
      {renderPipelineDialog()}
      {renderJoinedDialog()}
      {renderOffersDialog()}
      <OvjRecruiterDrillDialog
        open={!!recruiterDrill}
        onOpenChange={(open) => { if (!open) setRecruiterDrill(null); }}
        recruiterId={recruiterDrill?.recruiter_id}
        recruiterName={recruiterDrill?.recruiter_name}
        metric={recruiterDrill?.metric}
        period={period}
        startDate={startDate}
        endDate={endDate}
        joinBasis={joinBasis}
      />
    </Card>
  );
};

export default OfferVsJoiningCard;
